'use client'

import { useMemo, useState } from 'react'

import { ContentFilter } from '@/components/common/content-filter/content-filter'
import { FilterPills } from '@/components/common/content-filter/filter-pills'
import { Button } from '@/components/ui/button'

import { PortfolioCard } from './portfolio-card'
import {
  isCommercialProject,
  PORTFOLIO_SORT_OPTIONS,
  type PortfolioCategory,
  type PortfolioProject,
  type WorkType,
} from './portfolio-types'

interface PortfolioListingProps {
  projects: PortfolioProject[]
  categories: PortfolioCategory[]
  detailBasePath: string
  searchPlaceholder?: string
}

/**
 * The live client-side filter + grid shared by /gallery and /projects. Both
 * pages fetch up to 200 published projects server-side and hand the full list
 * here; search, category, and sort all run in the browser against that list
 * (no server round trip, no pagination), exactly as /gallery already worked
 * before this task. detailBasePath lets each listing keep its own existing
 * card link convention (/gallery/:slug vs /projects/:slug).
 *
 * The work-type switch narrows the list before ContentFilter sees it, so
 * category, search, and sort all operate within the chosen type. It sits
 * outside ContentFilter and above the collapsed filter bar for two reasons:
 * commercial vs personal is a portfolio concept (the shared filter also serves
 * articles and products), and branded fleet work is the lead offer, so it
 * should be reachable without opening the drawer.
 */
export function PortfolioListing({
  projects,
  categories,
  detailBasePath,
  searchPlaceholder = 'Search projects...',
}: PortfolioListingProps) {
  const [workType, setWorkType] = useState<WorkType>('all')

  const counts = useMemo(() => {
    const commercial = projects.filter(isCommercialProject).length
    return { all: projects.length, commercial, personal: projects.length - commercial }
  }, [projects])

  const visible = useMemo(() => {
    if (workType === 'all') return projects
    const wantCommercial = workType === 'commercial'
    return projects.filter((p) => isCommercialProject(p) === wantCommercial)
  }, [projects, workType])

  // A type with nothing behind it would strand the visitor on an empty grid, so
  // the switch only renders once both sides have work to show.
  const showSwitch = counts.commercial > 0 && counts.personal > 0

  const workTypeOptions = [
    { label: `All work (${counts.all})`, value: 'all' },
    { label: `Commercial (${counts.commercial})`, value: 'commercial' },
    { label: `Personal (${counts.personal})`, value: 'personal' },
  ]

  return (
    <div>
      {showSwitch && (
        <div className="mx-auto max-w-[1440px] px-4 pt-4 sm:px-6 lg:px-8">
          <FilterPills
            label="Work type"
            options={workTypeOptions}
            value={workType}
            onChange={(value) => setWorkType(value as WorkType)}
          />
        </div>
      )}

      <ContentFilter
        items={visible}
        categories={categories}
        sortOptions={PORTFOLIO_SORT_OPTIONS}
        searchFields={['title', 'description']}
        categoryAccessor={(p) => p.categorySlug}
        searchPlaceholder={searchPlaceholder}
        renderItems={(filtered) => (
          <PaginatedGrid projects={filtered} detailBasePath={detailBasePath} />
        )}
      />
    </div>
  )
}

const PAGE_SIZE = 24

function PaginatedGrid({
  projects,
  detailBasePath,
}: {
  projects: PortfolioProject[]
  detailBasePath: string
}) {
  const [page, setPage] = useState(1)
  // A new filter result restarts at page 1; keying on the ids means a
  // re-sort of the same set also resets, which is what a visitor expects.
  const resultKey = projects.map((p) => p.id).join('|')
  const [seenKey, setSeenKey] = useState(resultKey)
  if (seenKey !== resultKey) {
    setSeenKey(resultKey)
    setPage(1)
  }

  const pageCount = Math.max(1, Math.ceil(projects.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * PAGE_SIZE
  const visible = projects.slice(start, start + PAGE_SIZE)

  return (
    <section className="mx-auto max-w-[1200px] px-6 py-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((project) => (
          <PortfolioCard key={project.id} project={project} detailBasePath={detailBasePath} />
        ))}
      </div>
      {pageCount > 1 && (
        <nav aria-label="Gallery pages" className="mt-10 flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="secondary"
            isDisabled={current === 1}
            onPress={() => setPage(current - 1)}
            aria-label="Previous page"
          >
            Previous
          </Button>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <Button
              key={n}
              variant={n === current ? 'primary' : 'tertiary'}
              onPress={() => setPage(n)}
              aria-label={`Page ${n}`}
              aria-current={n === current ? 'page' : undefined}
            >
              {n}
            </Button>
          ))}
          <Button
            variant="secondary"
            isDisabled={current === pageCount}
            onPress={() => setPage(current + 1)}
            aria-label="Next page"
          >
            Next
          </Button>
          <span className="text-muted ml-2 text-sm">
            {start + 1}-{Math.min(start + PAGE_SIZE, projects.length)} of {projects.length}
          </span>
        </nav>
      )}
    </section>
  )
}
